import { Button, notify, useConfirmModal } from '@affine/component';
import * as reviewStyles from '@affine/core/modules/permissions/views/permission-review-list.css';
import { ShareInfoService } from '@affine/core/modules/share-doc';
import { PublicDocMode } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';

export const PublicDoc = ({ disabled }: { disabled?: boolean }) => {
  const t = useI18n();
  const sharing = useService(ShareInfoService).shareInfo;
  const info = useLiveData(sharing.info$);
  const refreshing = useLiveData(sharing.isRevalidating$);
  const mutating = useLiveData(sharing.isMutating$);
  const error = useLiveData(sharing.error$);
  const state = info?.publicSharing;
  const { openConfirmModal } = useConfirmModal();
  if (!state)
    return (
      <div role="status" className={reviewStyles.notice}>
        {t['com.streamlain.share-menu.public-sharing.error']()}
      </div>
    );
  const locked = disabled || refreshing || mutating || Boolean(error);
  const change = async (enable: boolean) => {
    try {
      if (enable) await sharing.enableShare(PublicDocMode.Page);
      else await sharing.disableShare();
      notify.success({
        title: t['com.streamlain.share-menu.public-sharing.updated'](),
      });
    } catch {
      notify.error({
        title: t['com.streamlain.share-menu.public-sharing.error'](),
      });
    }
  };
  const otherSources = state.otherSourcesCount > 0;
  const revokeLabel =
    t[
      otherSources
        ? 'com.streamlain.share-menu.public-sharing.revoke-manual'
        : 'com.streamlain.share-menu.public-sharing.revoke'
    ]();
  return (
    <div>
      <div className={reviewStyles.pagination}>
        <strong data-testid="public-sharing-status" aria-live="polite">
          {t[
            error
              ? 'com.streamlain.share-menu.public-sharing.unknown'
              : state.public
                ? 'com.streamlain.share-menu.public-sharing.on'
                : 'com.streamlain.share-menu.public-sharing.off'
          ]()}
        </strong>
      </div>
      <div className={reviewStyles.notice}>
        {t['com.streamlain.share-menu.public-sharing.description']()}
      </div>
      {otherSources ? (
        <div className={reviewStyles.notice} role="status">
          {t['com.streamlain.share-menu.public-sharing.other-sources']({
            count: String(state.otherSourcesCount),
          })}
        </div>
      ) : null}
      {state.manual ? (
        <Button
          variant="secondary"
          disabled={locked || !state.canRevoke}
          data-testid="share-link-menu-disable-share"
          onClick={() => {
            openConfirmModal({
              title: revokeLabel,
              description: otherSources
                ? t['com.streamlain.share-menu.public-sharing.other-sources']({
                    count: String(state.otherSourcesCount),
                  })
                : t[
                    'com.streamlain.share-menu.public-sharing.revoke-description'
                  ](),
              confirmText: revokeLabel,
              cancelText: t['Cancel'](),
              onConfirm: () => change(false),
            });
          }}
        >
          {revokeLabel}
        </Button>
      ) : !state.public ? (
        <>
          <Button
            variant="secondary"
            disabled={locked || !state.canEnable}
            data-testid="share-link-menu-enable-share"
            onClick={() => void change(true)}
          >
            {t['com.streamlain.share-menu.public-sharing.enable']()}
          </Button>
          {!state.canEnable ? (
            <div className={reviewStyles.notice}>
              {t['com.streamlain.share-menu.public-sharing.restricted']()}
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
};
